import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, DollarSign, Upload, FileText, X, CheckCircle } from 'lucide-react';
import { usePOCancellation } from '@/hooks/usePOCancellation';
import { POModificationRequest, formatCurrency } from '@/lib/types/job';
import { CancellationDocument } from '@/lib/types/cancellation';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VendorCancellationResponseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cancellationRequest: POModificationRequest & {
    purchase_orders: {
      po_amount_cents: number;
      po_number: string;
    };
  };
  onResponseSubmitted?: () => void;
  jobPublicId?: string;
}

export const VendorCancellationResponseDialog = ({
  open,
  onOpenChange,
  cancellationRequest,
  onResponseSubmitted,
  jobPublicId,
}: VendorCancellationResponseDialogProps) => {
  const navigate = useNavigate();
  const [responseType, setResponseType] = useState<'no_fee' | 'with_fee' | null>(null);
  const [feeAmount, setFeeAmount] = useState('');
  const [feeExplanation, setFeeExplanation] = useState('');
  const [documents, setDocuments] = useState<File[]>([]);
  const { submitVendorResponse } = usePOCancellation();

  // Defensive check for purchase_orders data
  if (!cancellationRequest?.purchase_orders) {
    return null;
  }

  const poAmount = cancellationRequest.purchase_orders.po_amount_cents;
  const feeCents = Math.round(parseFloat(feeAmount || '0') * 100);
  const refundAmount = poAmount - feeCents;
  const maxFee = Math.floor(poAmount * 0.5);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setDocuments(Array.from(e.target.files));
    }
  };

  const removeDocument = (index: number) => {
    setDocuments(docs => docs.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (responseType === 'no_fee') {
      submitVendorResponse.mutate(
        {
          cancellationRequestId: cancellationRequest.id,
          acceptWithNoFee: true,
        },
        {
          onSuccess: () => {
            onResponseSubmitted?.();
            onOpenChange(false);
            resetForm();
            // Redirect to job details page to show updated status (full refund = immediate cancellation)
            if (jobPublicId) {
              navigate(`/jobs/${jobPublicId}`);
            }
          },
        }
      );
    } else if (responseType === 'with_fee') {
      try {
        // Upload documents to Supabase Storage
        const supportingDocuments: CancellationDocument[] = [];
        
        if (documents.length > 0) {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error('User not authenticated');

          for (const doc of documents) {
            const fileExt = doc.name.split('.').pop();
            const fileName = `${user.id}/${cancellationRequest.id}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
            
            const { error: uploadError } = await supabase.storage
              .from('cancellation-documents')
              .upload(fileName, doc);

            if (uploadError) throw uploadError;
            
            // Get public URL
            const { data: { publicUrl } } = supabase.storage
              .from('cancellation-documents')
              .getPublicUrl(fileName);
            
            supportingDocuments.push({
              file_name: doc.name,
              file_url: publicUrl,
              file_type: doc.type,
              size_bytes: doc.size,
            });
          }
        }

        submitVendorResponse.mutate(
          {
            cancellationRequestId: cancellationRequest.id,
            acceptWithNoFee: false,
            cancellationChargeCents: feeCents,
            feeExplanation,
            supportingDocuments: supportingDocuments.length > 0 ? supportingDocuments : undefined,
          },
          {
            onSuccess: () => {
              onResponseSubmitted?.();
              onOpenChange(false);
              resetForm();
            },
          }
        );
      } catch (error) {
        console.error('Error uploading documents:', error);
        toast.error('Failed to upload documents. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setResponseType(null);
    setFeeAmount('');
    setFeeExplanation('');
    setDocuments([]);
  };

  const isValid = () => {
    if (responseType === 'no_fee') return true;
    if (responseType === 'with_fee') {
      return (
        feeCents > 0 &&
        feeCents <= maxFee &&
        feeExplanation.trim().length >= 50 &&
        (feeCents <= 100000 || documents.length > 0)
      );
    }
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Respond to Cancellation Request</DialogTitle>
          <DialogDescription>
            The client has requested to cancel PO {cancellationRequest.purchase_orders.po_number}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Client's Reason:</strong> {(cancellationRequest.request_details as any)?.reason || 'No reason provided'}
            </AlertDescription>
          </Alert>

          {!responseType && (
            <div className="grid grid-cols-2 gap-4">
              <Card 
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => setResponseType('no_fee')}
              >
                <CardHeader>
                  <CardTitle className="text-lg">Accept with Full Refund</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Accept the cancellation and refund the full amount to the client.
                  </p>
                  <p className="text-lg font-semibold mt-2 text-green-600">
                    {formatCurrency(poAmount)} refund
                  </p>
                </CardContent>
              </Card>

              <Card 
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => setResponseType('with_fee')}
              >
                <CardHeader>
                  <CardTitle className="text-lg">Propose Cancellation Fee</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Charge a fee for work completed or materials purchased.
                  </p>
                  <p className="text-sm mt-2 text-muted-foreground">
                    Max: {formatCurrency(maxFee)} (50%)
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {responseType === 'no_fee' && (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setResponseType(null)}
              >
                ← Change Response Type
              </Button>

              <Card className="border-green-200 bg-green-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Accept with Full Refund
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    You are accepting the cancellation request and agreeing to refund the full PO amount to the client.
                  </p>
                  
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Refund Amount:</span>
                      <span className="text-xl font-bold text-green-600">
                        {formatCurrency(poAmount)}
                      </span>
                    </div>
                  </div>

                  <Alert variant="default">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      This action cannot be undone. The PO will be cancelled and the full amount will be refunded to the client.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          )}

          {responseType === 'with_fee' && (
            <div className="space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setResponseType(null)}
              >
                ← Change Response Type
              </Button>

              <div className="space-y-2">
                <Label htmlFor="fee">Cancellation Fee *</Label>
                <Input
                  id="fee"
                  type="number"
                  step="0.01"
                  min="0"
                  max={maxFee / 100}
                  placeholder="0.00"
                  value={feeAmount}
                  onChange={(e) => setFeeAmount(e.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Maximum: {formatCurrency(maxFee)} (50% of PO)
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Fee Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Original PO Amount:</span>
                    <span className="font-medium">{formatCurrency(poAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Cancellation Fee:</span>
                    <span className="font-medium text-destructive">
                      -{formatCurrency(feeCents)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                    <span>Refund to Client:</span>
                    <span className="text-green-600">{formatCurrency(refundAmount)}</span>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Label htmlFor="explanation">Fee Explanation * (min 50 characters)</Label>
                <Textarea
                  id="explanation"
                  placeholder="Provide a detailed explanation for the cancellation fee, including work completed, materials purchased, or other costs incurred..."
                  value={feeExplanation}
                  onChange={(e) => setFeeExplanation(e.target.value)}
                  rows={5}
                  className="resize-none"
                />
                <p className="text-sm text-muted-foreground">
                  {feeExplanation.length} / 50 characters minimum
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="documents">
                  Supporting Documents {feeCents > 100000 && '*'}
                </Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors">
                  <Input
                    id="documents"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label htmlFor="documents" className="cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload invoices, receipts, or work documentation
                    </p>
                  </label>
                </div>
                {feeCents > 100000 && (
                  <p className="text-sm text-destructive">
                    * Supporting documents required for fees over $1,000
                  </p>
                )}
                {documents.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span className="text-sm">{doc.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(doc.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDocument(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitVendorResponse.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid() || submitVendorResponse.isPending}
          >
            {submitVendorResponse.isPending ? 'Submitting...' : 'Submit Response'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
